import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { adminService } from '@/api/services/admin';
import type { User } from '@/api/services/admin';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DEFAULT_MODELS, modelsById, PRICING_META, type ModelRate } from '@/lib/cogs/pricing';
import {
  DEFAULT_ACTIONS,
  defaultSelection,
  allSlots,
  type CogsAction,
} from '@/lib/cogs/actions';
import {
  actionCost,
  creditEconomics,
  stripeFee,
  summarizeMeasured,
  subscriptionMonthlyCost,
  blendedPerCredit,
  type CogsUsageRow,
} from '@/lib/cogs/compute';
import { fetchUserUsage, type SyncMeta } from '@/lib/cogs/sync';
import { sampleUsageRows } from '@/lib/cogs/sampleUsage';
import { promptCost, textChars, PROMPT_TEMPLATES, type PromptTemplateRef } from '@/lib/cogs/promptCost';

// ── Formatting ────────────────────────────────────────────────────────────────

function usd(n: number): string {
  const abs = Math.abs(n);
  const digits = abs === 0 ? 2 : abs < 1 ? 4 : abs < 100 ? 2 : 0;
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function usd2(n: number): string {
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function pctStr(n: number): string {
  return (n * 100).toFixed(0) + '%';
}

// ── Cost-share bar (Dashboard HBar idiom) ─────────────────────────────────────

function CostBar({ label, value, total, colorClass = 'bg-primary-500' }: { label: string; value: number; total: number; colorClass?: string }) {
  const widthPct = total <= 0 ? 0 : Math.max(2, Math.round((value / total) * 100));
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-text-secondary w-40 flex-shrink-0 truncate" title={label}>{label}</span>
      <div className="flex-1 h-4 bg-surface rounded-sm overflow-hidden">
        <div className={`h-full ${colorClass} rounded-sm transition-all duration-300`} style={{ width: `${widthPct}%` }} />
      </div>
      <span className="text-xs text-text-primary tabular-nums w-28 text-right flex-shrink-0">
        {usd(value)} <span className="text-text-muted">({total <= 0 ? '0%' : pctStr(value / total)})</span>
      </span>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function COGS() {
  const actions = DEFAULT_ACTIONS;
  const actionsById = useMemo(() => Object.fromEntries(actions.map((a) => [a.id, a])) as Record<string, CogsAction>, [actions]);

  const [models, setModels] = useState<ModelRate[]>(() => DEFAULT_MODELS.map((m) => ({ ...m })));
  const [selection, setSelection] = useState<Record<string, string>>(() => defaultSelection(actions));
  const [proPrice, setProPrice] = useState(99);
  const [includedCredits, setIncludedCredits] = useState(100);

  // Platform monthly roll-up inputs. There is no cross-user aggregate endpoint,
  // so the platform figure is a config-driven monthly model rather than a live
  // sum: subscription plans are the certain, dominant cost; metered LLM spend is
  // seeded from real billing and editable. See the "Platform monthly cost" card.
  // Metered LLM spend, USD/mo — seeded from actual billing, editable.
  const [geminiMonthly, setGeminiMonthly] = useState(1.34); // Google Cloud billing: $4.02 / 3mo
  const [claudeMonthly, setClaudeMonthly] = useState(0); // set from Anthropic console
  // Monthly usage per subscription vendor (calls or chars) → drives overage.
  const [subUsage, setSubUsage] = useState<Record<string, number>>({});

  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState('');

  const [usageRows, setUsageRows] = useState<CogsUsageRow[] | null>(null);
  const [usageMode, setUsageMode] = useState<'none' | 'live' | 'sample'>('none');
  const [syncUserId, setSyncUserId] = useState('');
  const [syncMeta, setSyncMeta] = useState<SyncMeta | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');

  // Prompt-cost lens
  const [lensTemplate, setLensTemplate] = useState<PromptTemplateRef>(PROMPT_TEMPLATES[0]);
  const [lensBaseChars, setLensBaseChars] = useState(0);
  const [lensInjected, setLensInjected] = useState(PROMPT_TEMPLATES[0].sampleInjected);
  const [lensOutputChars, setLensOutputChars] = useState(6000);
  const [lensCpt, setLensCpt] = useState(4);
  const [lensModelId, setLensModelId] = useState('');
  const [lensGrounded, setLensGrounded] = useState(PROMPT_TEMPLATES[0].grounded);
  const [lensLoadedChars, setLensLoadedChars] = useState<number | null>(null);
  const [lensLoading, setLensLoading] = useState(false);
  const [lensError, setLensError] = useState('');

  useEffect(() => {
    adminService
      .getUsers()
      .then(setUsers)
      .catch((e) => setUsersError(e instanceof Error ? e.message : 'Failed to load users.'))
      .finally(() => setLoadingUsers(false));
  }, []);

  const byId = useMemo(() => modelsById(models), [models]);
  const revenuePerCredit = includedCredits > 0 ? proPrice / includedCredits : 0;

  // Modeled per-action unit cost from default usage assumptions + current selection.
  const perActionCost = useMemo(
    () => actions.map((a) => actionCost(a, selection, byId)),
    [actions, selection, byId],
  );
  const econ = useMemo(
    () => creditEconomics(actions, selection, byId, revenuePerCredit),
    [actions, selection, byId, revenuePerCredit],
  );

  const subscribedCount = users.filter((u) => u.status.subscription.is_subscribed).length;
  const monthlyStripe = subscribedCount * stripeFee(proPrice, byId);
  const monthlyRevenue = subscribedCount * proPrice;

  // ── Platform monthly cost roll-up ─────────────────────────────────────────
  // Subscription vendors (Apollo, ElevenLabs): fixed plan + overage on the
  // month's usage. Metered LLM (Gemini, Claude): editable, billing-sourced.
  const subMonthly = useMemo(
    () =>
      models
        .filter((m) => m.kind === 'subscription')
        .map((m) => ({ model: m, calc: subscriptionMonthlyCost(m, subUsage[m.id] ?? 0) })),
    [models, subUsage],
  );
  const subMonthlyTotal = subMonthly.reduce((sum, s) => sum + s.calc.total, 0);
  const meteredMonthly = geminiMonthly + claudeMonthly;
  const platformMonthlyTotal = subMonthlyTotal + meteredMonthly + monthlyStripe;
  const grossMarginMonthly = monthlyRevenue - platformMonthlyTotal;

  // Measured summary (after Sync / sample).
  const measured = useMemo(
    () => (usageRows ? summarizeMeasured(usageRows, actionsById, selection, byId) : null),
    [usageRows, actionsById, selection, byId],
  );

  // Prompt-cost lens: price a real template + injected payload under a model.
  const lensModelIdEff = lensModelId || selection[lensTemplate.slot] || '';
  const lensResult = useMemo(
    () =>
      promptCost(byId[lensModelIdEff], {
        baseChars: lensBaseChars,
        injectedChars: lensInjected.length,
        outputChars: lensOutputChars,
        charsPerToken: lensCpt,
        grounded: lensGrounded,
      }),
    [byId, lensModelIdEff, lensBaseChars, lensInjected, lensOutputChars, lensCpt, lensGrounded],
  );

  const handleSelectTemplate = (key: string) => {
    const t = PROMPT_TEMPLATES.find((p) => p.key === key) ?? PROMPT_TEMPLATES[0];
    setLensTemplate(t);
    setLensInjected(t.sampleInjected);
    setLensGrounded(t.grounded);
    setLensModelId('');
    setLensBaseChars(0);
    setLensLoadedChars(null);
    setLensError('');
  };
  const handleLoadTemplate = async () => {
    setLensLoading(true);
    setLensError('');
    try {
      const cfg = await adminService.getConfig(lensTemplate.configName);
      const chars = textChars(cfg);
      setLensBaseChars(chars);
      setLensLoadedChars(chars);
    } catch (e) {
      setLensError(e instanceof Error ? e.message : 'Failed to load template.');
    } finally {
      setLensLoading(false);
    }
  };

  const handleSync = async () => {
    const uid = syncUserId.trim();
    if (!uid) {
      setSyncError('Enter a user ID to sync.');
      return;
    }
    setSyncing(true);
    setSyncError('');
    try {
      const { rows, meta } = await fetchUserUsage(uid);
      setUsageRows(rows);
      setSyncMeta(meta);
      setUsageMode('live');
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };
  const handleLoadSample = () => {
    setUsageRows(sampleUsageRows());
    setSyncMeta(null);
    setUsageMode('sample');
    setSyncError('');
  };
  const handleClearUsage = () => {
    setUsageRows(null);
    setSyncMeta(null);
    setUsageMode('none');
    setSyncError('');
  };

  const setRate = (id: string, field: keyof ModelRate, value: number) => {
    setModels((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  };
  const resetPricing = () => setModels(DEFAULT_MODELS.map((m) => ({ ...m })));

  const slots = allSlots(actions);
  const lensCandidates = slots.find((s) => s.slot === lensTemplate.slot)?.candidateModelIds ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">COGS explorer</h1>
          <p className="text-sm text-text-secondary mt-1 max-w-2xl">
            Cost of goods sold per credit-consuming action. Swap the model behind each call to see how
            unit cost and margin move. <strong>Platform monthly cost</strong> rolls up the real vendor bill
            (subscriptions + metered LLM + Stripe); <strong>Sync</strong> pulls one user's real per-call usage from{' '}
            <code className="font-mono text-xs bg-surface px-1 py-0.5 rounded">get_api_logs</code> +{' '}
            <code className="font-mono text-xs bg-surface px-1 py-0.5 rounded">get_token_usage</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={usageMode === 'live' ? 'success' : usageMode === 'sample' ? 'warning' : 'neutral'} size="sm">
            {usageMode === 'live' ? 'Measured · live' : usageMode === 'sample' ? 'Measured · sample' : 'Modeled'}
          </Badge>
          <input
            value={syncUserId}
            onChange={(e) => setSyncUserId(e.target.value)}
            placeholder="user ID to sync…"
            className="w-56 rounded-md border border-border bg-surface px-2 py-1.5 text-xs font-mono text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <Button variant="primary" size="sm" disabled={syncing} onClick={handleSync}>
            {syncing ? 'Syncing…' : 'Sync user'}
          </Button>
        </div>
      </div>

      {/* Sync error → offer sample fallback */}
      {syncError && (
        <div className="p-3 rounded-md border border-warning-200 dark:border-warning-800/30 bg-warning-50 dark:bg-warning-900/20 flex items-center gap-3 flex-wrap">
          <p className="text-sm text-warning-800 dark:text-warning-300 flex-1">
            {syncError} — or load sample per-call data to explore the measured view.
          </p>
          <Button variant="secondary" size="sm" onClick={handleLoadSample}>Load sample data</Button>
        </div>
      )}

      {/* Assumptions row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent>
            <label className="block text-xs text-text-secondary uppercase tracking-wider mb-1">Pro price / mo</label>
            <div className="flex items-center gap-1">
              <span className="text-text-muted">$</span>
              <input type="number" min={0} value={proPrice} onChange={(e) => setProPrice(Number(e.target.value) || 0)}
                className="w-24 rounded-md border border-border bg-surface px-2 py-1 text-lg font-semibold text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <label className="block text-xs text-text-secondary uppercase tracking-wider mb-1">Included credits</label>
            <input type="number" min={1} value={includedCredits} onChange={(e) => setIncludedCredits(Number(e.target.value) || 1)}
              className="w-24 rounded-md border border-border bg-surface px-2 py-1 text-lg font-semibold text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Revenue / credit</p>
            <p className="text-2xl font-bold text-text-primary tabular-nums">{usd(revenuePerCredit)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Subscribers</p>
            <p className="text-2xl font-bold text-text-primary tabular-nums">{loadingUsers ? '…' : subscribedCount}</p>
            <p className="text-xs text-text-muted mt-1">{usd2(monthlyRevenue)}/mo revenue</p>
          </CardContent>
        </Card>
      </div>

      {/* Model swap */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Model per call</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary mb-4">Pick the model powering each driver. Everything below recomputes instantly.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {slots.map((slot) => (
              <div key={slot.slot}>
                <label className="block text-xs font-medium text-text-primary mb-1">{slot.label}</label>
                <select
                  value={selection[slot.slot]}
                  onChange={(e) => setSelection((prev) => ({ ...prev, [slot.slot]: e.target.value }))}
                  className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {slot.candidateModelIds.map((id) => (
                    <option key={id} value={id}>{byId[id]?.label ?? id}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Unit economics per credit */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Unit economics per credit</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-elevated">
                  {['Action', 'Credits', 'COGS / action', 'COGS / credit', 'Rev / credit', 'Margin / credit', 'Margin %'].map((c) => (
                    <th key={c} className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wide whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {econ.costPerCreditByAction.map((r) => {
                  const marginPct = econ.revenuePerCredit > 0 ? r.marginPerCredit / econ.revenuePerCredit : 0;
                  const bad = r.marginPerCredit < 0;
                  return (
                    <tr key={r.actionId} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 text-text-primary">{r.label}</td>
                      <td className="px-4 py-2.5 text-text-secondary tabular-nums">{r.credits}</td>
                      <td className="px-4 py-2.5 text-text-primary tabular-nums">{usd(r.unitCost)}</td>
                      <td className="px-4 py-2.5 text-text-primary tabular-nums">{usd(r.costPerCredit)}</td>
                      <td className="px-4 py-2.5 text-text-secondary tabular-nums">{usd(econ.revenuePerCredit)}</td>
                      <td className={`px-4 py-2.5 tabular-nums ${bad ? 'text-error-600 dark:text-error-400' : 'text-success-700 dark:text-success-400'}`}>{usd(r.marginPerCredit)}</td>
                      <td className="px-4 py-2.5 tabular-nums">
                        <Badge variant={bad ? 'error' : marginPct > 0.7 ? 'success' : 'warning'} size="xs">{pctStr(marginPct)}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-text-muted italic px-4 py-3">
            Modeled from default per-call token assumptions. Revenue/credit = Pro price ÷ included credits.
            A negative margin means that action costs more than the credit sells for.
          </p>
        </CardContent>
      </Card>

      {/* Per-action modeled breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Modeled cost per action — driver breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {perActionCost.map((ac) => (
            <div key={ac.action.id}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-text-primary">
                  {ac.action.label}
                  {ac.action.credits > 0 && <span className="text-text-muted font-normal"> · {ac.action.credits} cr</span>}
                </span>
                <span className="text-sm text-text-primary tabular-nums">{usd(ac.unitCost)}</span>
              </div>
              <div className="space-y-1.5">
                {ac.steps.map((s) => (
                  <CostBar key={s.slot} label={`${s.label} — ${s.modelLabel}`} value={s.cost} total={ac.unitCost || 1} />
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Prompt-cost lens */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Prompt-cost lens</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary mb-4">
            The input prompt drives most of an LLM call's cost. Load a real template from the Prompts config,
            paste a representative injected payload, and see how much the runtime variables add — and what the
            same prompt would cost on a different model.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: inputs */}
            <div className="space-y-4">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-text-secondary mb-1">Prompt template</label>
                  <select
                    value={lensTemplate.key}
                    onChange={(e) => handleSelectTemplate(e.target.value)}
                    className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {PROMPT_TEMPLATES.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <Button variant="secondary" size="sm" disabled={lensLoading} onClick={handleLoadTemplate}>
                  {lensLoading ? 'Loading…' : 'Load template'}
                </Button>
              </div>
              {lensError && <p className="text-xs text-error-700 dark:text-error-400">{lensError}</p>}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    Base template chars
                    {lensLoadedChars != null && <span className="text-success-700 dark:text-success-400"> · loaded {lensLoadedChars.toLocaleString()}</span>}
                  </label>
                  <input type="number" min={0} step={100} value={lensBaseChars}
                    onChange={(e) => { setLensBaseChars(Number(e.target.value) || 0); setLensLoadedChars(null); }}
                    className="w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Expected output chars</label>
                  <input type="number" min={0} step={500} value={lensOutputChars}
                    onChange={(e) => setLensOutputChars(Number(e.target.value) || 0)}
                    className="w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500" />
                </div>
              </div>

              <div>
                <label className="block text-xs text-text-secondary mb-1">
                  Injected variables (paste a representative payload) · <span className="tabular-nums">{lensInjected.length.toLocaleString()} chars</span>
                </label>
                <textarea
                  rows={6}
                  value={lensInjected}
                  onChange={(e) => setLensInjected(e.target.value)}
                  className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-xs font-mono text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
                />
              </div>

              <div className="grid grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Model</label>
                  <select
                    value={lensModelIdEff}
                    onChange={(e) => setLensModelId(e.target.value)}
                    className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {lensCandidates.map((id) => (
                      <option key={id} value={id}>{byId[id]?.label ?? id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Chars / token</label>
                  <input type="number" min={1} step={0.5} value={lensCpt}
                    onChange={(e) => setLensCpt(Number(e.target.value) || 4)}
                    className="w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500" />
                </div>
                <label className="flex items-center gap-2 text-sm text-text-primary pb-1.5">
                  <input type="checkbox" checked={lensGrounded} onChange={(e) => setLensGrounded(e.target.checked)}
                    className="rounded border-border text-primary-500 focus:ring-primary-500" />
                  Grounded
                </label>
              </div>
            </div>

            {/* Right: results */}
            <div className="rounded-lg border border-border bg-surface-elevated p-4 space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-text-secondary">Cost per call</span>
                <span className="text-2xl font-bold text-text-primary tabular-nums">{usd(lensResult.total)}</span>
              </div>
              <div className="space-y-1.5 pt-1">
                <CostBar label={`Base input — ${lensResult.baseInputTokens.toLocaleString()} tok`} value={lensResult.baseInputCost} total={lensResult.total || 1} colorClass="bg-primary-500" />
                <CostBar label={`Injected input — ${lensResult.injectedInputTokens.toLocaleString()} tok`} value={lensResult.injectedInputCost} total={lensResult.total || 1} colorClass="bg-warning-500" />
                <CostBar label={`Output — ${lensResult.outputTokens.toLocaleString()} tok`} value={lensResult.outputCost} total={lensResult.total || 1} colorClass="bg-success-500" />
                {lensResult.groundingCost > 0 && (
                  <CostBar label="Grounding surcharge" value={lensResult.groundingCost} total={lensResult.total || 1} colorClass="bg-info-500" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border text-sm">
                <div>
                  <p className="text-xs text-text-secondary uppercase tracking-wider mb-0.5">Total input</p>
                  <p className="text-text-primary tabular-nums">{lensResult.inputTokens.toLocaleString()} tok</p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary uppercase tracking-wider mb-0.5">Injected share</p>
                  <p className="text-text-primary tabular-nums">
                    {pctStr(lensResult.injectedTokenShare)} of input · {pctStr(lensResult.injectedCostShare)} of cost
                  </p>
                </div>
              </div>
              <p className="text-xs text-text-muted italic pt-1">
                Tokens estimated at {lensCpt} chars/token (Gemini/Claude ≈ 4). Grounding uses the model's per-request search surcharge.
                A high injected share means per-prospect variables — not the template — drive cost.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform monthly cost — real vendor bill roll-up */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Platform monthly cost — the real vendor bill</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-elevated">
                  {['Vendor', 'Basis', "Monthly usage", 'Fixed', 'Overage', 'Monthly cost'].map((c) => (
                    <th key={c} className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wide whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Subscription vendors — fixed plan + overage on the month's usage */}
                {subMonthly.map(({ model: m, calc }) => (
                  <tr key={m.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5 text-text-primary whitespace-nowrap">
                      {m.label}
                      {calc.utilization > 0 && (
                        <Badge variant={calc.utilization > 1 ? 'error' : calc.utilization > 0.85 ? 'warning' : 'neutral'} size="xs" className="ml-2">
                          {pctStr(calc.utilization)} of plan
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary text-xs">
                      {usd2(m.monthlyCost ?? 0)}/mo · {(m.includedCredits ?? 0).toLocaleString()} cr incl.
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-1">
                        <input
                          type="number" min={0} step={m.unit === 'char' ? 10000 : 100}
                          value={subUsage[m.id] ?? 0}
                          onChange={(e) => setSubUsage((prev) => ({ ...prev, [m.id]: Number(e.target.value) || 0 }))}
                          className="w-28 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <span className="text-text-muted text-xs">{m.unit}s</span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary tabular-nums">{usd2(calc.fixed)}</td>
                    <td className={`px-4 py-2.5 tabular-nums ${calc.overage > 0 ? 'text-warning-700 dark:text-warning-400' : 'text-text-muted'}`}>
                      {calc.overage > 0 ? usd2(calc.overage) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-text-primary tabular-nums font-medium">{usd2(calc.total)}</td>
                  </tr>
                ))}
                {/* Metered LLM — editable, seeded from real billing */}
                <tr className="border-b border-border">
                  <td className="px-4 py-2.5 text-text-primary whitespace-nowrap">
                    Gemini <Badge variant="success" size="xs" className="ml-2">metered</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary text-xs">Google Cloud billing ($4.02 / 3mo)</td>
                  <td className="px-4 py-2.5 text-text-muted text-xs italic">per token</td>
                  <td className="px-4 py-2.5 text-text-muted">—</td>
                  <td className="px-4 py-2.5 text-text-muted">—</td>
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-1">
                      <span className="text-text-muted">$</span>
                      <input type="number" min={0} step={0.5} value={geminiMonthly}
                        onChange={(e) => setGeminiMonthly(Number(e.target.value) || 0)}
                        className="w-24 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500" />
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2.5 text-text-primary whitespace-nowrap">
                    Claude <Badge variant="success" size="xs" className="ml-2">metered</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary text-xs">Anthropic console (set from invoice)</td>
                  <td className="px-4 py-2.5 text-text-muted text-xs italic">per token</td>
                  <td className="px-4 py-2.5 text-text-muted">—</td>
                  <td className="px-4 py-2.5 text-text-muted">—</td>
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-1">
                      <span className="text-text-muted">$</span>
                      <input type="number" min={0} step={1} value={claudeMonthly}
                        onChange={(e) => setClaudeMonthly(Number(e.target.value) || 0)}
                        className="w-24 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500" />
                    </span>
                  </td>
                </tr>
                {/* Stripe — real monthly recurring at today's subscriber count */}
                <tr className="border-b border-border">
                  <td className="px-4 py-2.5 text-text-primary whitespace-nowrap">Stripe processing</td>
                  <td className="px-4 py-2.5 text-text-secondary text-xs">
                    {loadingUsers ? '…' : `${subscribedCount} subs × ${usd2(stripeFee(proPrice, byId))}`}
                  </td>
                  <td className="px-4 py-2.5 text-text-muted text-xs italic">per charge</td>
                  <td className="px-4 py-2.5 text-text-muted">—</td>
                  <td className="px-4 py-2.5 text-text-muted">—</td>
                  <td className="px-4 py-2.5 text-text-primary tabular-nums font-medium">{usd2(monthlyStripe)}</td>
                </tr>
                <tr className="border-t-2 border-border font-semibold bg-surface-elevated">
                  <td className="px-4 py-2.5 text-text-primary" colSpan={5}>Total monthly COGS</td>
                  <td className="px-4 py-2.5 text-text-primary tabular-nums">{usd2(platformMonthlyTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 border-t border-border">
            <div>
              <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Revenue / mo</p>
              <p className="text-xl font-bold text-text-primary tabular-nums">{loadingUsers ? '…' : usd2(monthlyRevenue)}</p>
              <p className="text-xs text-text-muted mt-1">{subscribedCount} subs × {usd2(proPrice)}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Total COGS / mo</p>
              <p className="text-xl font-bold text-text-primary tabular-nums">{usd2(platformMonthlyTotal)}</p>
              <p className="text-xs text-text-muted mt-1">subscriptions + metered + Stripe</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Gross margin / mo</p>
              <p className={`text-xl font-bold tabular-nums ${grossMarginMonthly < 0 ? 'text-error-600 dark:text-error-400' : 'text-success-700 dark:text-success-400'}`}>
                {usd2(grossMarginMonthly)}
              </p>
              <p className="text-xs text-text-muted mt-1">{monthlyRevenue > 0 ? `${pctStr(grossMarginMonthly / monthlyRevenue)} of revenue` : '—'}</p>
            </div>
          </div>
          {usersError && <p className="text-xs text-error-700 dark:text-error-400 px-4 pb-2">{usersError}</p>}
          <p className="text-xs text-text-muted italic px-4 pb-3">
            No cross-user aggregate endpoint exists, so this is a monthly cost model, not a live sum. The two subscriptions ($99 each) are the certain, dominant cost;
            set each vendor's monthly usage to project overage. Gemini is seeded from actual Google Cloud billing; set Claude from your Anthropic invoice. Nothing is persisted.
          </p>
        </CardContent>
      </Card>

      {/* Measured (post-sync) */}
      {measured && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Measured COGS — {usageMode === 'sample' ? 'sample data' : syncMeta ? `user ${syncMeta.userId.slice(0, 8)}…` : 'synced usage'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={handleClearUsage}>Clear</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4">
              <div><p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Total COGS</p><p className="text-xl font-bold text-text-primary tabular-nums">{usd2(measured.grandTotal)}</p></div>
              <div><p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Users</p><p className="text-xl font-bold text-text-primary tabular-nums">{measured.userCount}</p></div>
              <div><p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Median / user</p><p className="text-xl font-bold text-text-primary tabular-nums">{usd(measured.medianUserCost)}</p></div>
              <div><p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Mean / user</p><p className="text-xl font-bold text-text-primary tabular-nums">{usd(measured.meanUserCost)}</p></div>
            </div>
            <div className="overflow-x-auto border-t border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-elevated">
                    {['Action', 'Calls', 'Total COGS', 'Mean / call', 'Median / call'].map((c) => (
                      <th key={c} className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wide whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {measured.perAction.map((r) => (
                    <tr key={r.actionId} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 text-text-primary">{r.label}</td>
                      <td className="px-4 py-2.5 text-text-secondary tabular-nums">{r.calls.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-text-primary tabular-nums">{usd2(r.totalCost)}</td>
                      <td className="px-4 py-2.5 text-text-secondary tabular-nums">{usd(r.meanCostPerCall)}</td>
                      <td className="px-4 py-2.5 text-text-secondary tabular-nums">{usd(r.medianCostPerCall)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-text-muted italic px-4 py-3">
              Observed per-call tokens re-priced under the models currently selected above — change a model to project what this user's usage would cost on a different provider.
              {syncMeta && (
                <>
                  {' '}Synced {syncMeta.logRows} log rows across {syncMeta.pages} page{syncMeta.pages === 1 ? '' : 's'};
                  {' '}{syncMeta.tokenMatched} calls matched exact token counts (rest from response body or char estimate).
                  {syncMeta.truncated && ' Results truncated — user has more history than one sync pulled.'}
                </>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Vendor pricing editor */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Vendor pricing</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetPricing}>Reset to defaults</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-elevated">
                  {['Model', 'Vendor', 'Input $/1M', 'Output $/1M', 'Ground $/call', 'Flat $/call', '$/1k chars', '% + fixed', ''].map((c) => (
                    <th key={c} className="px-3 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wide whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {models.filter((m) => m.kind !== 'subscription').map((m) => (
                  <tr key={m.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 text-text-primary whitespace-nowrap">{m.label}</td>
                    <td className="px-3 py-2 text-text-secondary">{m.vendor}</td>
                    <td className="px-3 py-2">{rateInput(m, 'inputPerM', ['llm', 'embedding'], setRate)}</td>
                    <td className="px-3 py-2">{rateInput(m, 'outputPerM', ['llm'], setRate)}</td>
                    <td className="px-3 py-2">{rateInput(m, 'groundingPerCall', ['llm'], setRate)}</td>
                    <td className="px-3 py-2">{rateInput(m, 'perCall', ['per_call', 'llm'], setRate)}</td>
                    <td className="px-3 py-2">{rateInput(m, 'perThousandChars', ['per_char'], setRate)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {m.kind === 'payment' ? (
                        <span className="flex items-center gap-1">
                          {rateInput(m, 'percentFee', ['payment'], setRate, 0.001)}
                          <span className="text-text-muted">+</span>
                          {rateInput(m, 'fixedFee', ['payment'], setRate)}
                        </span>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={m.confidence === 'current' ? 'success' : 'warning'} size="xs">
                        {m.confidence === 'current' ? 'current' : 'est.'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-text-muted italic px-4 py-3">
            Model, embedding &amp; Perplexity rates are fed from <strong>{PRICING_META.source}</strong> (ref <code className="font-mono text-xs">{PRICING_META.ref}</code>, fetched {PRICING_META.fetchedAt}) — refresh with <code className="font-mono text-xs">npm run update-prices</code>. Apollo &amp; ElevenLabs are subscription plans (configured below). Edits apply live to every figure above; nothing is persisted.
          </p>
        </CardContent>
      </Card>

      {/* Subscription vendors (Apollo, ElevenLabs) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subscription plans (Apollo, ElevenLabs)</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary mb-4">
            A flat monthly cost buys an included credit allotment; usage beyond it bills at the overage rate.
            Per-action COGS above uses the <strong>blended rate</strong> (plan ÷ included credits). Set the plan, allotment,
            credit conversion, and overage to match your invoices.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {models.filter((m) => m.kind === 'subscription').map((m) => {
              const perCredit = blendedPerCredit(m);
              const perUnit = perCredit * (m.creditsPerUnit ?? 1);
              return (
                <div key={m.id} className="rounded-lg border border-border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-text-primary">{m.label}</span>
                    <Badge variant="primary" size="xs">{m.vendor}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <SubField label="Monthly cost ($)">{rateInput(m, 'monthlyCost', ['subscription'], setRate, 1)}</SubField>
                    <SubField label="Included credits / mo">{rateInput(m, 'includedCredits', ['subscription'], setRate, 1000)}</SubField>
                    <SubField label={`Credits per ${m.unit}`}>{rateInput(m, 'creditsPerUnit', ['subscription'], setRate, 0.5)}</SubField>
                    <SubField label="Overage ($/1k credits)">{rateInput(m, 'overagePer1kCredits', ['subscription'], setRate, 0.01)}</SubField>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-border text-xs">
                    <span className="text-text-secondary">Blended</span>
                    <span className="text-text-primary tabular-nums">
                      {usd(perCredit * 1000)}/1k credits · {usd(perUnit * 1000)}/1k {m.unit}s
                    </span>
                  </div>
                  {m.note && <p className="text-xs text-text-muted italic">{m.note}</p>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Labeled field wrapper for the subscription editor ─────────────────────────

function SubField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-text-secondary mb-1">{label}</label>
      {children}
    </div>
  );
}

// ── Rate input (only rendered for applicable model kinds) ─────────────────────

function rateInput(
  m: ModelRate,
  field: keyof ModelRate,
  kinds: ModelRate['kind'][],
  setRate: (id: string, field: keyof ModelRate, value: number) => void,
  step = 0.01,
) {
  if (!kinds.includes(m.kind)) return <span className="text-text-muted">—</span>;
  const val = (m[field] as number | undefined) ?? 0;
  return (
    <input
      type="number"
      min={0}
      step={step}
      value={val}
      onChange={(e) => setRate(m.id, field, Number(e.target.value) || 0)}
      className="w-20 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500"
    />
  );
}
